import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoePrints } from '@fortawesome/free-solid-svg-icons';
import styles from './TeamList.module.css';

interface TeamMember {
  id: number;
  name: string;
  level: number;
  race?: string;
  sex?: string;
  avatar?: number;
  healthPct?: number;
  isReady?: boolean;
  isBot?: boolean;
}

const MemberAvatar: React.FC<{ member: TeamMember }> = ({ member }) => {
  const [imgFailed, setImgFailed] = React.useState(false);
  if (!imgFailed && member.race && member.sex) {
    return (
      <img
        className={styles.memberAvatarImg}
        src={`/img/avatars/${member.race.toLowerCase()}.${member.sex.toLowerCase()}.${member.avatar ?? 1}.png`}
        alt={member.name}
        onError={() => setImgFailed(true)}
      />
    );
  }
  return <div className={styles.memberAvatar}>{member.name.charAt(0).toUpperCase()}</div>;
};

interface TeamListProps {
  teamOne: TeamMember[];
  teamTwo: TeamMember[];
  ownerMercenaryId: number;
  isOwner: boolean;
  handleKickPlayer: (id: number) => void;
  randomTeams: boolean;
  pve: boolean;
  maxPlayers?: number;
  myMercenaryId?: number;
}

const MemberRow: React.FC<{
  member: TeamMember;
  isOwner: boolean;
  isMe: boolean;
  onKick: () => void;
}> = ({ member, isOwner, isMe, onKick }) => {
  const hp = member.healthPct ?? 100;
  const hpColor = hp > 60 ? '#4caf72' : hp > 30 ? '#c9a227' : '#c0392b';

  return (
    <li className={`${styles.memberSlot} ${styles.memberSlotFilled}`}>
      <div className={styles.memberAvatarWrapper}>
        <MemberAvatar member={member} />
        {isOwner && !isMe && !member.isBot && (
          <button
            className={styles.kickOverlay}
            onClick={(e) => { e.stopPropagation(); onKick(); }}
            title="Kick"
          ><FontAwesomeIcon icon={faShoePrints} /></button>
        )}
      </div>
      <div className={styles.memberInfo}>
        <span className={styles.memberName}>
          {member.isBot ? (
            <span className={styles.memberNameLink}>{member.name}</span>
          ) : (
            <Link to={`/mercenary/${member.id}`} className={styles.memberNameLink}>
              {member.name}
            </Link>
          )}
          {member.isBot && <span className={styles.botBadge}>B</span>}
          {isMe && <span className={styles.youBadge}> (you)</span>}
        </span>
        <span className={styles.memberLevel}>Lv {member.level}</span>
        <div className={styles.hpBarWrap}>
          <div
            className={styles.hpBarFill}
            style={{ width: `${Math.min(100, hp)}%`, background: hpColor }}
          />
          <span className={styles.hpLabel}>{hp.toFixed(0)}%</span>
        </div>
      </div>
    </li>
  );
};

const EmptySlot: React.FC = () => (
  <li className={styles.memberSlot}>
    <div className={styles.memberAvatarWrapper}>
      <div className={styles.memberAvatar} style={{ opacity: 0.2 }}>?</div>
    </div>
    <span className={styles.emptySlot}>Empty slot</span>
  </li>
);

const TeamList: React.FC<TeamListProps> = ({
  teamOne, teamTwo, isOwner, handleKickPlayer, randomTeams, pve, maxPlayers,
  myMercenaryId, ownerMercenaryId,
}: TeamListProps & { maxPlayers?: number }) => {
  const slotsPerTeam = maxPlayers ? Math.floor(maxPlayers / 2) : Math.max(teamOne.length, teamTwo.length, 2);

  const sortedTeamOne = [...teamOne].sort((a, b) =>
    a.id === ownerMercenaryId ? -1 : b.id === ownerMercenaryId ? 1 : 0
  );
  const sortedTeamTwo = [...teamTwo].sort((a, b) =>
    a.id === ownerMercenaryId ? -1 : b.id === ownerMercenaryId ? 1 : 0
  );

  const renderTeam = (members: TeamMember[], slots: number) =>
    Array.from({ length: slots }).map((_, i) => {
      const m = members[i];
      return m
        ? <MemberRow key={m.id} member={m} isOwner={isOwner} isMe={m.id === myMercenaryId} onKick={() => handleKickPlayer(m.id)} />
        : <EmptySlot key={`empty-${i}`} />;
    });

  // Random teams: show all participants as paired slots (left/right) with no team labels
  if (randomTeams && !pve) {
    const allMembers = [...teamOne, ...teamTwo].sort((a, b) =>
      a.id === ownerMercenaryId ? -1 : b.id === ownerMercenaryId ? 1 : 0
    );
    const totalSlots = maxPlayers ?? Math.max(allMembers.length, 2);
    const leftSlots = Math.ceil(totalSlots / 2);
    const rightSlots = Math.floor(totalSlots / 2);
    const leftMembers = allMembers.filter((_, i) => i % 2 === 0);
    const rightMembers = allMembers.filter((_, i) => i % 2 === 1);

    return (
      <div className={styles.teamsContainer}>
        <div className={styles.teamColumn}>
          <div className={`${styles.teamHeader} ${styles.teamHeaderNeutral}`}>Players</div>
          <ul className={styles.teamMembersList}>
            {Array.from({ length: leftSlots }).map((_, i) => {
              const m = leftMembers[i];
              return m
                ? <MemberRow key={m.id} member={m} isOwner={isOwner} isMe={m.id === myMercenaryId} onKick={() => handleKickPlayer(m.id)} />
                : <EmptySlot key={`empty-l-${i}`} />;
            })}
          </ul>
        </div>
        <div className={styles.teamColumn}>
          <div className={`${styles.teamHeader} ${styles.teamHeaderNeutral}`}>Players</div>
          <ul className={styles.teamMembersList}>
            {Array.from({ length: rightSlots }).map((_, i) => {
              const m = rightMembers[i];
              return m
                ? <MemberRow key={m.id} member={m} isOwner={isOwner} isMe={m.id === myMercenaryId} onKick={() => handleKickPlayer(m.id)} />
                : <EmptySlot key={`empty-r-${i}`} />;
            })}
          </ul>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.teamsContainer}>
      <div className={styles.teamColumn}>
        <div className={`${styles.teamHeader} ${styles.teamHeaderA}`}>Team One</div>
        <ul className={styles.teamMembersList}>
          {renderTeam(sortedTeamOne, pve ? (sortedTeamOne.length || 1) : slotsPerTeam)}
        </ul>
      </div>
      <div className={styles.teamColumn}>
        <div className={`${styles.teamHeader} ${pve ? styles.teamHeaderPvE : styles.teamHeaderB}`}>
          {pve ? 'Enemies' : 'Team Two'}
        </div>
        <ul className={styles.teamMembersList}>
          {pve
            ? <li className={`${styles.memberSlot} ${styles.memberSlotFilled}`}><span className={styles.emptySlot}>Beast (auto)</span></li>
            : renderTeam(sortedTeamTwo, slotsPerTeam)}
        </ul>
      </div>
    </div>
  );
};

export default TeamList;
